import { QueryClient } from "@tanstack/react-query";
import { onProjectStatusRecovery } from "@/domains/projects/status/projectStatusAvailability";

/**
 * Module-level singleton so non-React code (MST stores, repository functions
 * that perform writes) can flush react-query caches after the underlying DB
 * write succeeds. Imported once by App.tsx for the QueryClientProvider, and
 * by any code that needs to invalidate after writes.
 *
 * staleTime is 5 minutes globally — without explicit invalidation after a
 * write, cached views (like the Trash list) keep showing pre-write state
 * until the user crosses the staleness window.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

/** A failed availability probe may have cached active fallback project rows. */
export function refreshProjectQueriesOnStatusRecovery(client: QueryClient): () => void {
  return onProjectStatusRecovery(() => {
    for (const queryKey of [
      ["building-projects"],
      ["invite-org-projects"],
      ["org-projects-overview"],
    ]) {
      // A pending first fetch has no cached data, so invalidateQueries alone
      // reuses its fallback promise. Cancel it before starting the fresh read.
      void client.cancelQueries({ queryKey }).then(() => client.invalidateQueries({ queryKey }));
    }
  });
}

refreshProjectQueriesOnStatusRecovery(queryClient);
